import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Entity } from '@backstage/catalog-model';
import { Config } from '@backstage/config';
import {
  Coalition,
  allEnvs,
  coalitionForEntity,
  resolveCoalition,
  tierForEnv,
} from '../coalitions';

// Shared env state for the multi-env dashboard. Persisted in two
// places: the URL as ?env=<name> AND sessionStorage. Backstage's tab
// navigation drops the current query string, so URL alone loses state
// on every tab switch; sessionStorage covers that gap.
//
// Read priority: URL query > sessionStorage > first env of the
// coalition. Write: setEnv updates both.
const STORAGE_KEY = 'envDashboard.env';

function readStoredEnv(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredEnv(env: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, env);
  } catch {
    /* ignore */
  }
}

export function useEnv(
  entity: Entity,
  config: Config,
): {
  coalition: Coalition | undefined;
  env: string;
  tier: 'staging' | 'production' | undefined;
  setEnv: (env: string) => void;
} {
  const location = useLocation();
  const navigate = useNavigate();

  // Only the tiers this entity has links for (a production-only app opens on production).
  const resolvedCoalition = resolveCoalition(entity, config);
  const coalition = resolvedCoalition
    ? coalitionForEntity(resolvedCoalition, entity.metadata.annotations)
    : undefined;
  const envs = coalition ? allEnvs(coalition) : [];
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get('env');
  const fromStorage = readStoredEnv();
  const raw = fromUrl ?? fromStorage ?? '';
  const env = envs.includes(raw) ? raw : envs[0] ?? '';

  const resolved = coalition ? tierForEnv(env, coalition) : undefined;

  const setEnv = React.useCallback(
    (next: string) => {
      writeStoredEnv(next);
      const nextParams = new URLSearchParams(location.search);
      nextParams.set('env', next);
      navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
    },
    [location.pathname, location.search, navigate],
  );

  return { coalition, env, tier: resolved?.tier, setEnv };
}
